/**
 * Prognostic-indices card (FIB-4 / MELD / KFRE). Surfaces validated prognostic
 * instruments the backend computes and stamps into the organ overall row's
 * `structured_fields.progression` — previously computed but never shown. Each row
 * shows the value, a severity-coloured category (colour + glyph, never colour
 * alone) and a plain-language meaning. KFRE carries a visible PROVISIONAL caveat:
 * its baseline is not yet clinically calibrated, so it must never read as a
 * validated risk.
 */

import { Activity } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../ui/utils';
import {
  BAND_CHIP,
  categoryBand,
  PROGNOSTIC_META,
  type PrognosticIndex,
} from './clinicalStaging';
import { BAND_STYLES } from './organHealth';
import { S2Card } from './S2Card';

function formatValue(idx: PrognosticIndex): string {
  if (idx.value == null || idx.value === '') return '—';
  const unit = idx.units === 'percent' ? '%' : idx.units ? ` ${idx.units}` : '';
  const horizon = idx.horizon_years ? ` at ${idx.horizon_years}y` : '';
  return `${idx.value}${unit}${horizon}`;
}

function IndexRow({ idx }: { idx: PrognosticIndex }) {
  const meta = PROGNOSTIC_META[idx.name] ?? { label: idx.name.toUpperCase(), meaning: '' };
  const band = categoryBand(idx.category);
  const style = BAND_STYLES[band];
  const Icon = style.icon;

  return (
    <div className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-text-primary">{meta.label}</span>
          <span className="text-sm tabular-nums text-text-secondary">{formatValue(idx)}</span>
        </div>
        {meta.meaning ? (
          <p className="mt-0.5 text-[12px] leading-snug text-text-tertiary">{meta.meaning}</p>
        ) : null}
        {idx.provisional ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="mt-1 inline-flex w-fit cursor-help items-center gap-1 rounded border border-band-mild/40 bg-band-mild-bg px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-band-mild-text"
                aria-label="Provisional — baseline not yet clinically calibrated"
              >
                Provisional
              </span>
            </TooltipTrigger>
            <TooltipContent variant="clinical">
              <div className="max-w-[16rem] text-[11px] leading-snug">
                The baseline for this equation is not yet clinically calibrated. Interpret as
                directional only — not a validated risk estimate.
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {idx.category ? (
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
            BAND_CHIP[band],
          )}
          aria-label={`Category: ${idx.category}`}
        >
          <Icon aria-hidden className="size-3" />
          <span className="capitalize">{idx.category}</span>
          {style.glyph ? (
            <span aria-hidden className="opacity-70 leading-none">
              {style.glyph}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** Renders nothing when no prognostic index is available (fail-quiet). */
export function PrognosticIndicesCard({ indices }: { indices: PrognosticIndex[] }) {
  const rows = indices.filter((i) => i.value != null && i.value !== '');
  if (!rows.length) return null;
  return (
    <S2Card title="Prognostic indices" count={rows.length} unit="index" icon={Activity}>
      <div className="divide-y divide-border/45">
        {rows.map((idx) => (
          <IndexRow key={idx.name} idx={idx} />
        ))}
      </div>
      <p className="pt-1 text-[11px] leading-snug text-text-tertiary">
        Validated instruments computed from the chart. Decision-support, not a validated clinical
        instrument for prognosis on its own.
      </p>
    </S2Card>
  );
}
